import {
  Col,
  Row,
  Space,
  Table,
  Card,
  Spin,
  message,
  Divider,
  Button,
  Typography,
  Select,
  Input,
  InputNumber,
  Tag,
  Dropdown,
} from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FilterOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  SearchOutlined,
  DownOutlined,
  PrinterOutlined,
  ReloadOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import "jspdf-autotable";
import XLSX from "xlsx-js-style";
import {
  HEADERS_EXPORT_BEN,
  POST_MAIN_LOST_PRE_ENFORCEMENT,
  POST_SEND_PRE_ENFORCEMENT,
} from "../../API/apiUrls";
import CurrencyFormat from "../../../hook/CurrencyFormat";
import useLawyerNamesNew from "../../../hook/UseLawyerNameNew";
import "../../../assets/font/THSarabunNew-normal";
import "../../../assets/font/THSarabunNew-bold";
import MotionHoc from "../../../utils/MotionHoc";
import TokenCheck from "../../../hook/TokenCheck";

const { Text } = Typography;

const OPTIONS_TYPE = [
  { value: "lsfhp", label: "จำนอง สัญญา 1" },
  { value: "vsfhp", label: "เช่าซื้อบัญชี 2" },
  { value: "sfhp", label: "เช่าซื้อบัญชี 8" },
  { value: "rpsl", label: "P-LOAN" },
];

const OPTIONS_TYPE_WITH_ALL = [
  { value: "all", label: "ทั้งหมด" },
  ...OPTIONS_TYPE,
];

const getSchemaLabel = (dataType) =>
  OPTIONS_TYPE.find((o) => o.value === dataType)?.label || dataType || "-";

const getCaseTypeLabel = (flagLoanNew) =>
  flagLoanNew === 1 ? "ทำยอม" : flagLoanNew === 2 ? "พิพากษา" : "-";

const formatThaiDate = (d) =>
  d ? dayjs(d).add(543, "year").format("DD/MM/YYYY") : "-";

// วันที่รูปแบบ export ตามตัวอย่าง (2026-03-05)
const formatDateExport = (d) => (d ? dayjs(d).format("YYYY-MM-DD") : "-");

const Main = () => {
  const userId = parseInt(localStorage.getItem("USER_ID"));
  const [, , currencyFormatPoint] = CurrencyFormat();
  const [signOut] = TokenCheck();
  const { nameLawyers, loadingUseLawyerNames } = useLawyerNamesNew({
    auto: true,
    toast: false,
  });

  const [loading, setLoading] = useState(false);
  const [arrayPay, setArrayPay] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [schemaFilter, setSchemaFilter] = useState("all");
  const [searchContno, setSearchContno] = useState("");
  const [expMin, setExpMin] = useState(null);
  const [expMax, setExpMax] = useState(null);
  const [lawyerName, setLawyerName] = useState(undefined);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isModal, setIsModal] = useState(false);

  const getRowKey = useCallback(
    (r) => r.loanNewId ?? `${r.DATA_TYPE}_${r.CONTNO}`,
    [],
  );

  const lawyerOptions = useMemo(
    () =>
      Array.from(
        new Set(
          (nameLawyers ?? [])
            .filter(
              (l) =>
                (l.ROLE_ID === 3 || l.ROLE_ID === 4) &&
                l.ACTIVE_STATUS &&
                l.COMPANY_ID !== 3,
            )
            .map((l) => l.NNAME)
            .filter(Boolean),
        ),
      ).map((name) => ({ value: name, label: name })),
    [nameLawyers],
  );

  // ── Query ─────────────────────────────────────────────
  const queryData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        POST_MAIN_LOST_PRE_ENFORCEMENT,
        { userId: 1, flagLoanNew: 2 },
        { headers: HEADERS_EXPORT_BEN },
      );
      const data = Array.isArray(res.data.mixData) ? res.data.mixData : [];
      setArrayPay(data);
      setFilteredData(data);
    } catch (err) {
      message.error(
        err?.response?.status === 404 ? "ไม่พบข้อมูล" : "เกิดข้อผิดพลาด",
      );
      if (err?.response?.status === 401) {
        signOut();
      }
      setArrayPay([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  }, [signOut]);

  useEffect(() => {
    queryData();
  }, []);

  // ── Filter (มีผลเฉพาะตอนกดปุ่ม "กรองข้อมูล") ──────────
  const handleFilter = useCallback(() => {
    let filtered = arrayPay;

    if (schemaFilter !== "all") {
      filtered = filtered.filter((d) => d.DATA_TYPE === schemaFilter);
    }
    if (lawyerName) {
      filtered = filtered.filter((d) => d.SALECOD === lawyerName);
    }
    if (expMin != null) {
      filtered = filtered.filter((d) => (d.EXPREAL ?? 0) >= expMin);
    }
    if (expMax != null) {
      filtered = filtered.filter((d) => (d.EXPREAL ?? 0) <= expMax);
    }

    const query = searchContno.trim().toLowerCase();
    if (query) {
      filtered = filtered.filter(
        (d) =>
          d.CONTNO?.toLowerCase().includes(query) ||
          d.CODE_CONTNO?.toLowerCase().includes(query) ||
          d.ISSUNO?.toLowerCase().includes(query),
      );
    }

    setFilteredData(filtered);
    setSelectedRowKeys([]);
  }, [arrayPay, schemaFilter, lawyerName, expMin, expMax, searchContno]);

  // ── กำหนดผู้รับผิดชอบ ─────────────────────────────────
  const handleAssignSuccess = useCallback(
    (assignedRows) => {
      const assignMap = new Map(
        assignedRows.map((row) => [row.key, row.lawyerName]),
      );
      const applyAssign = (list) =>
        list.map((r) =>
          assignMap.has(getRowKey(r))
            ? { ...r, SALECOD: assignMap.get(getRowKey(r)) }
            : r,
        );

      setArrayPay((prev) => applyAssign(prev));
      setFilteredData((prev) => applyAssign(prev));
      setSelectedRowKeys([]);
    },
    [getRowKey],
  );

  const handleBulkSave = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning("กรุณาเลือกรายการที่ต้องการดำเนินการ");
      return;
    }

    // ✅ หา records ที่ถูกเลือก
    const selectedRecords = arrayPay.filter((item) =>
      selectedRowKeys.includes(getRowKey(item)),
    );

    setLoading(true);
    try {
      const payload = {
        userId,
        all_idJudge: selectedRecords.map((r) => ({ idJudge: r.idJudge })),
      };
      console.log("payload", payload);

      await postSendPreEnforce(payload, POST_SEND_PRE_ENFORCEMENT);

      message.success(`สำเร็จ ${selectedRecords.length} รายการ`);
      setSelectedRowKeys([]);
      queryData(); // โหลดข้อมูลใหม่
    } catch (err) {
      console.error("❌ Error in bulk save:", err);
    } finally {
      setLoading(false);
    }
  };

  const postSendPreEnforce = async (payload, apiUse) => {
    console.log("📤 postSendPreEnforce with data:", payload);

    try {
      const res = await axios.post(apiUse, payload, {
        headers: HEADERS_EXPORT_BEN,
      });

      const { status, data } = res;

      if (status === 200) {
        console.log("✅ API Response:", data);
        return data;
      }
    } catch (error) {
      const status = error?.response?.status;
      const serverMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error.message;

      if (status === 404) {
        message.error("ไม่พบปลายทาง (404)");
      } else if (status === 400) {
        message.error(`ข้อมูลไม่ถูกต้อง: ${serverMsg}`);
      } else if (status === 500) {
        message.error("เกิดข้อผิดพลาดจากเซิร์ฟเวอร์ (500)");
      } else {
        message.error(
          `ไม่สามารถส่งข้อมูลได้${status ? ` (${status})` : ""}: ${serverMsg}`,
        );
      }

      console.error("❌ postSendPreEnforce error:", error);
      throw error;
    }
  };

  const hasActiveFilters =
    schemaFilter !== "all" ||
    !!lawyerName ||
    expMin != null ||
    expMax != null ||
    !!searchContno.trim();

  const handleResetFilters = useCallback(() => {
    setSchemaFilter("all");
    setSearchContno("");
    setExpMin(null);
    setExpMax(null);
    setLawyerName(undefined);
    setFilteredData(arrayPay);
    setSelectedRowKeys([]);
  }, [arrayPay]);

  // ── Columns ───────────────────────────────────────────
  const columns = [
    {
      title: "ลำดับ",
      key: "index",
      align: "center",
      width: 60,
      render: (_, __, i) => i + 1,
    },
    {
      title: "รายละเอียด",
      key: "detail",
      align: "left",
      width: 260,
      render: (_, r) => (
        <div style={{ fontSize: 13, lineHeight: 1.7 }}>
          <div>
            <Text strong>เลขสัญญา {r.CONTNO || "-"}</Text>
          </div>
          <div>
            {`${r.SNAM || ""}${r.NAME1 || ""} ${r.NAME2 || ""}`.trim() || "-"}
          </div>
          <div style={{ color: "#888" }}>
            {getSchemaLabel(r.DATA_TYPE)} ・ เลขโฉนด/เลขตัวถัง {r.STRNO || "-"}
          </div>
          <div style={{ color: "#888" }}>ผู้รับผิดชอบ: {r.SALECOD}</div>
        </div>
      ),
    },
    {
      title: "สถานะ",
      key: "flagLoanNew",
      align: "center",
      width: 90,
      render: (_, r) => (
        <Tag color={r.flagLoanNew === 1 ? "gold" : "blue"}>
          {getCaseTypeLabel(r.flagLoanNew)}
        </Tag>
      ),
    },
    {
      title: "การชำระ",
      key: "payment",
      align: "left",
      width: 200,
      render: (_, r) => (
        <div style={{ fontSize: 13, lineHeight: 1.7 }}>
          <div>ยอดชำระ : {currencyFormatPoint(r.TOTPRC)} บาท</div>
          <div>วันที่ทำสัญญา : {formatThaiDate(r.SDATE)}</div>
          <div>วันที่ก่อนคิดเบี้ยปรับ : {formatThaiDate(r.FDATE)}</div>
        </div>
      ),
    },
    {
      title: "เบี้ยปรับ",
      key: "RES_BEEYPUP",
      align: "left",
      width: 110,
      render: (_, r) => (
        <div style={{ fontSize: 13, lineHeight: 1.6 }}>
          <div>
            <span style={{ color: "#888" }}>เบี้ยปรับ </span>
            {currencyFormatPoint(r.BEEYPUP) ?? "-"}
          </div>
          <div>
            <span style={{ color: "#888" }}>จ่ายแล้ว </span>
            <span style={{ color: "#52c41a" }}>
              {currencyFormatPoint(r.SMPAY_BEEYPUP) ?? "-"}
            </span>
          </div>
          <div>
            <span style={{ color: "#888" }}>คงเหลือ </span>
            <Text
              strong
              style={{ color: r.RES_BEEYPUP > 0 ? "#cf1322" : "#389e0d" }}
            >
              {currencyFormatPoint(r.RES_BEEYPUP) ?? "-"}
            </Text>
          </div>
          {r.PAY_BEEYPUP_DATE != null && (
            <div style={{ color: "#888", fontSize: 12 }}>
              ({formatThaiDate(r.PAY_BEEYPUP_DATE)})
            </div>
          )}
        </div>
      ),
    },
  ];

  // ── Export helpers ────────────────────────────────────
  const selectedRows = useMemo(
    () => filteredData.filter((r) => selectedRowKeys.includes(getRowKey(r))),
    [filteredData, selectedRowKeys, getRowKey],
  );

  // ไม่ได้เลือกแถวใด ๆ → export ข้อมูลที่กรองไว้ทั้งหมดแทน
  const exportRows = selectedRows.length ? selectedRows : filteredData;

  const exportMenuItems = {
    items: [
      {
        key: "pdf",
        label: "Export PDF",
        icon: <FilePdfOutlined style={{ color: "#c0392b" }} />,
      },
      {
        key: "excel",
        label: "Export Excel",
        icon: <FileExcelOutlined style={{ color: "#27ae60" }} />,
      },
    ],
    onClick: ({ key }) => {
      if (key === "pdf") handlePrintPDF();
      if (key === "excel") handleExportExcel();
    },
  };

  // คอลัมน์ตาม columns ของ Table (ลำดับ / รายละเอียด / สถานะ / การชำระ / เบี้ยปรับ)
  const moneyKeys = ["ยอดชำระ", "เบี้ยปรับ", "จ่ายแล้ว", "คงเหลือ"];

  const buildExportRows = () =>
    exportRows.map((r, i) => ({
      ลำดับ: i + 1,
      เลขสัญญา: r.CONTNO || "",
      "ชื่อ-นามสกุล": `${r.SNAM || ""}${r.NAME1 || ""} ${r.NAME2 || ""}`.trim(),
      บัญชี: getSchemaLabel(r.DATA_TYPE),
      "เลขโฉนด/เลขตัวถัง": r.STRNO || "",
      ผู้รับผิดชอบ: r.SALECOD || "",
      สถานะ: getCaseTypeLabel(r.flagLoanNew),
      ยอดชำระ: parseFloat(r.TOTPRC) || 0,
      วันที่ทำสัญญา: formatDateExport(r.SDATE),
      วันที่ก่อนคิดเบี้ยปรับ: formatDateExport(r.FDATE),
      เบี้ยปรับ: parseFloat(r.BEEYPUP) || 0,
      จ่ายแล้ว: parseFloat(r.SMPAY_BEEYPUP) || 0,
      คงเหลือ: parseFloat(r.RES_BEEYPUP) || 0,
      วันที่จ่ายเบี้ยปรับ: formatDateExport(r.PAY_BEEYPUP_DATE),
    }));

  const handleExportExcel = () => {
    const rows = buildExportRows();
    if (!rows.length) {
      message.warning("ไม่มีข้อมูลสำหรับ Export");
      return;
    }

    const summaryRow = {
      ลำดับ: "",
      เลขสัญญา: "",
      "ชื่อ-นามสกุล": `รวมทั้งหมด ${rows.length} รายการ`,
    };

    const allRows = [...rows, summaryRow];
    const ws = XLSX.utils.json_to_sheet(allRows);
    const keys = Object.keys(rows[0]);

    const evenFill = { patternType: "solid", fgColor: { rgb: "EBF5FF" } };
    const oddFill = { patternType: "solid", fgColor: { rgb: "FFFFFF" } };
    const summaryFill = { patternType: "solid", fgColor: { rgb: "D5E8D4" } };
    const borderStyle = {
      top: { style: "thin", color: { rgb: "CCCCCC" } },
      bottom: { style: "thin", color: { rgb: "CCCCCC" } },
      left: { style: "thin", color: { rgb: "CCCCCC" } },
      right: { style: "thin", color: { rgb: "CCCCCC" } },
    };

    // header
    keys.forEach((_, ci) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: ci });
      if (!ws[cellRef]) return;
      ws[cellRef].s = {
        fill: { patternType: "solid", fgColor: { rgb: "344962" } },
        font: { bold: true, color: { rgb: "FFFFFF" }, sz: 12 },
        alignment: { horizontal: "center", vertical: "center" },
        border: borderStyle,
      };
    });

    // data rows
    rows.forEach((row, ri) => {
      const fill = ri % 2 === 0 ? evenFill : oddFill;
      keys.forEach((key, ci) => {
        const cellRef = XLSX.utils.encode_cell({ r: ri + 1, c: ci });
        if (!ws[cellRef]) ws[cellRef] = { v: "", t: "s" };
        const isNumber = typeof row[key] === "number";
        const isMoney = moneyKeys.includes(key);
        ws[cellRef].s = {
          fill,
          font: { sz: 11 },
          alignment: {
            horizontal: isNumber ? "right" : "left",
            vertical: "center",
          },
          border: borderStyle,
          numFmt: isMoney ? "#,##0.00" : undefined,
        };
      });
    });

    // summary row
    const summaryRowIdx = rows.length + 1;
    keys.forEach((key, ci) => {
      const cellRef = XLSX.utils.encode_cell({ r: summaryRowIdx, c: ci });
      if (!ws[cellRef]) return;
      const isMoney = moneyKeys.includes(key);
      ws[cellRef].s = {
        fill: summaryFill,
        font: { bold: true, sz: 12 },
        alignment: {
          horizontal: isMoney ? "right" : "left",
          vertical: "center",
        },
        border: borderStyle,
        numFmt: isMoney ? "#,##0.00" : undefined,
      };
    });

    ws["!cols"] = keys.map((key) => ({ wch: Math.max(key.length * 2, 12) }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "รายงาน");
    XLSX.writeFile(
      wb,
      `รายงานหนี้ที่ขาดการติดต่อ_${dayjs().format("DDMMYYYY")}.xlsx`,
    );
    message.success("Export Excel สำเร็จ");
  };

  const handlePrintPDF = () => {
    if (!exportRows.length) {
      message.warning("ไม่มีข้อมูลสำหรับ Export");
      return;
    }

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });
    const pageWidth = doc.internal.pageSize.getWidth();
    const now = dayjs().format("DD/MM/YYYY HH:mm:ss");

    const drawHeader = (pageNum) => {
      doc.setFont("THSarabunNew", "bold");
      doc.setFontSize(16);
      doc.text("รายงานหนี้ที่ขาดการติดต่อ", pageWidth / 2, 8, {
        align: "center",
      });
      doc.setFont("THSarabunNew", "normal");
      doc.setFontSize(12);
      doc.text(`วันที่พิมพ์ ${now}`, 8, 16);
      doc.text(`หน้าที่ ${pageNum}`, pageWidth - 8, 16, { align: "right" });
    };

    const rows = buildExportRows();
    const tableBody = rows.map((r) =>
      Object.entries(r).map(([key, val]) =>
        // format ตัวเลขเงินให้มี comma ใน PDF
        moneyKeys.includes(key) ? currencyFormatPoint(val) : val,
      ),
    );

    doc.autoTable({
      startY: 20,
      head: [Object.keys(rows[0])],
      body: tableBody,
      headStyles: {
        font: "THSarabunNew",
        fillColor: [52, 73, 94],
        textColor: 255,
        halign: "center",
        fontStyle: "bold",
        fontSize: 10,
      },
      styles: {
        font: "THSarabunNew",
        fontSize: 9, // 14 คอลัมน์ ลดขนาด font ไม่ให้ล้นหน้า
        cellPadding: 1,
        halign: "center",
      },
      columnStyles: {
        2: { halign: "left" }, // ชื่อ-นามสกุล
        5: { halign: "left" }, // ผู้รับผิดชอบ
        7: { halign: "right" }, // ยอดชำระ
        10: { halign: "right" }, // เบี้ยปรับ
        11: { halign: "right" }, // จ่ายแล้ว
        12: { halign: "right" }, // คงเหลือ
      },
      alternateRowStyles: { fillColor: [235, 245, 255] },
      didDrawPage: () =>
        drawHeader(doc.internal.getCurrentPageInfo().pageNumber),
      margin: { top: 20, left: 3, right: 3 },
    });

    doc.save(`รายงานหนี้ที่ขาดการติดต่อ_${dayjs().format("DDMMYYYY")}.pdf`);
  };

  return (
    <>
      <style>{`
      .row-even td { background-color: #f5f8ff !important; }
      .row-odd td  { background-color: #ffffff !important; }
    `}</style>
      <Spin spinning={loading} size="large" tip="Loading...">
        <Card style={{ marginBottom: 10 }}>
          <Divider style={{ fontSize: 20 }}>รายงานหนี้ที่ขาดการติดต่อ</Divider>

          {/* ── แถบกรองข้อมูล ── */}
          <Card
            size="small"
            title={
              <Space>
                <FilterOutlined />
                <span>ตัวกรองข้อมูล</span>
              </Space>
            }
            style={{ marginBottom: 12, background: "#fafafa" }}
          >
            <Row gutter={[16, 12]} align="bottom">
              <Col xs={24} sm={12} md={6} lg={5}>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  บัญชี
                </Text>
                <Select
                  style={{ width: "100%" }}
                  options={OPTIONS_TYPE_WITH_ALL}
                  value={schemaFilter}
                  onChange={setSchemaFilter}
                  size="large"
                />
              </Col>

              <Col xs={24} sm={12} md={6} lg={5}>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  ค้นหาเลขที่สัญญา
                </Text>
                <Input
                  placeholder="เช่น V2-0012430"
                  value={searchContno}
                  onChange={(e) => setSearchContno(e.target.value)}
                  onPressEnter={handleFilter}
                  prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                  allowClear
                  size="small"
                />
              </Col>

              <Col xs={24} sm={12} md={6} lg={4}>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  ค้างงวด (จาก - ถึง)
                </Text>
                <Space.Compact style={{ width: "100%" }}>
                  <InputNumber
                    min={0}
                    placeholder="จาก"
                    value={expMin}
                    onChange={setExpMin}
                    style={{ width: "50%" }}
                    size="large"
                    disabled
                  />
                  <InputNumber
                    min={0}
                    placeholder="ถึง"
                    value={expMax}
                    onChange={setExpMax}
                    style={{ width: "50%" }}
                    size="large"
                    disabled
                  />
                </Space.Compact>
              </Col>

              <Col xs={24} sm={12} md={6} lg={4}>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  ผู้รับผิดชอบ
                </Text>
                <Select
                  style={{ width: "100%" }}
                  options={lawyerOptions}
                  value={lawyerName}
                  onChange={setLawyerName}
                  loading={loadingUseLawyerNames}
                  allowClear
                  showSearch
                  placeholder="ทั้งหมด"
                  size="large"
                />
              </Col>
              {/* 
              <Col xs={24} sm={12} md={6} lg={3}>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  ประเภท
                </Text>
                <div>
                  <Checkbox.Group
                    options={OPTIONS_CASE_TYPE}
                    value={caseTypeFilter}
                    onChange={setCaseTypeFilter}
                  />
                </div>
              </Col> */}

              <Col flex="auto" style={{ textAlign: "right" }}>
                <Space>
                  <Button
                    color="orange"
                    variant="outlined"
                    icon={<FilterOutlined />}
                    onClick={handleFilter}
                  >
                    กรองข้อมูล
                  </Button>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={handleResetFilters}
                    color="red"
                    variant="outlined"
                  >
                    ล้างตัวกรอง
                  </Button>
                  <Dropdown
                    menu={exportMenuItems}
                    disabled={!filteredData.length}
                  >
                    <Button icon={<PrinterOutlined />}>
                      Export <DownOutlined />
                    </Button>
                  </Dropdown>
                </Space>
              </Col>
            </Row>
          </Card>

          {/* ── สรุปตัวกรองที่ใช้อยู่ ── */}
          {hasActiveFilters && (
            <Card
              size="small"
              style={{ background: "#f0f5ff", marginBottom: 12 }}
            >
              <Space split="|" size="large" wrap>
                <span>
                  <strong>ผลการค้นหา:</strong> {filteredData.length} รายการ
                </span>
                {schemaFilter !== "all" && (
                  <span>
                    <strong>บัญชี:</strong> {getSchemaLabel(schemaFilter)}
                  </span>
                )}
                {searchContno && (
                  <span>
                    <strong>ค้นหา:</strong> "{searchContno}"
                  </span>
                )}
                {(expMin != null || expMax != null) && (
                  <span>
                    <strong>ค้างงวด:</strong> {expMin ?? 0} - {expMax ?? "∞"}
                  </span>
                )}
                {lawyerName && (
                  <span>
                    <strong>ผู้รับผิดชอบ:</strong>
                    {lawyerName}
                  </span>
                )}
              </Space>
            </Card>
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Button
              color="blue"
              variant="filled"
              icon={<PlusOutlined />}
              onClick={() => handleBulkSave()}
              disabled={selectedRows.length === 0}
              loading={loading}
              style={{ borderRadius: 8 }}
            >
              ส่งบังคับคดี{" "}
              {selectedRowKeys.length > 0 && ` (${selectedRowKeys.length})`}
            </Button>

            <Text
              strong
              style={{
                margin: "10px 0",
              }}
            >
              จำนวนสัญญาทั้งหมด : {arrayPay.length} เคส
              {selectedRowKeys.length > 0
                ? ` (เลือก ${selectedRowKeys.length} รายการ)`
                : " (ไม่เลือกรายการ = Export ข้อมูลทั้งหมด)"}
            </Text>
          </div>
          <Table
            style={{ marginTop: 10 }}
            size="small"
            columns={columns}
            dataSource={filteredData}
            rowKey={getRowKey}
            rowSelection={{
              selectedRowKeys,
              onChange: setSelectedRowKeys,
            }}
            rowClassName={(_, index) =>
              index % 2 === 0 ? "row-even" : "row-odd"
            }
            scroll={{ x: 1100 }}
            pagination={{
              defaultPageSize: 50,
              showSizeChanger: true,
              showTotal: (t) => `ทั้งหมด ${t} รายการ`,
              pageSizeOptions: ["10", "20", "50", "100"],
            }}
          />
        </Card>
      </Spin>
    </>
  );
};

const MainLostContactJudge = MotionHoc(Main);
export default MainLostContactJudge;
